// TCIO + Grayscale brand marks — vector recreations of the ratified 08-07 logo sheet.
// Variants: dark (light backgrounds) and light (navy sidebar).
import React from 'react';

const RingDefs = ({ id }) => (
  <defs>
    <linearGradient id={id} x1="0.15" y1="0" x2="0.85" y2="1">
      <stop offset="0" stopColor="#3D9BFF" />
      <stop offset="0.55" stopColor="#1E6FE0" />
      <stop offset="1" stopColor="#1A55C4" />
    </linearGradient>
  </defs>
);

// Ring-arrow "O": gap at 12 o'clock, arrow apex reaching into the gap, stem below.
export function TcioMark({ size = 34 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-label="TCIO">
      <RingDefs id="tcioRingM" />
      {/* ring with top gap: arc from ~ -78deg to ~ 258deg */}
      <path d="M 61.5 8.5 A 43 43 0 1 1 38.5 8.5"
        fill="none" stroke="url(#tcioRingM)" strokeWidth="12" strokeLinecap="round" />
      {/* "AI" monogram: A over an i (dot + stem) */}
      <path d="M 30 56 L 50 26 L 70 56" fill="none" stroke="url(#tcioRingM)"
        strokeWidth="9.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="50" cy="52" r="5.5" fill="url(#tcioRingM)" />
      <line x1="50" y1="63" x2="50" y2="76" stroke="url(#tcioRingM)"
        strokeWidth="9.5" strokeLinecap="round" />
    </svg>
  );
}

// Full lockup: TCI + ring + two-line subline with rules. tone: 'dark' | 'light'
export function TcioLockup({ height = 46, tone = 'dark' }) {
  const text = tone === 'light' ? '#FFFFFF' : '#123A6B';
  const sub = tone === 'light' ? 'rgba(255,255,255,0.78)' : '#1E3A5F';
  const rule = tone === 'light' ? 'rgba(255,255,255,0.45)' : '#1E3A5F';
  return (
    <svg height={height} viewBox="0 0 330 112" aria-label="TCIO — Treatment Centers Intelligence OS">
      <RingDefs id="tcioRingL" />
      <text x="0" y="60" fontFamily="Inter, sans-serif" fontWeight="800"
        fontSize="66" letterSpacing="-3" fill={text}>TCI</text>
      <g transform="translate(126, 4) scale(0.62)">
        <path d="M 61.5 8.5 A 43 43 0 1 1 38.5 8.5"
          fill="none" stroke="url(#tcioRingL)" strokeWidth="13" strokeLinecap="round" />
        <path d="M 30 56 L 50 26 L 70 56" fill="none" stroke="url(#tcioRingL)"
          strokeWidth="10.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="50" cy="52" r="6" fill="url(#tcioRingL)" />
        <line x1="50" y1="63" x2="50" y2="76" stroke="url(#tcioRingL)"
          strokeWidth="10.5" strokeLinecap="round" />
      </g>
      <text x="0" y="88" fontFamily="Inter, sans-serif" fontWeight="700"
        fontSize="18" letterSpacing="3.2" fill={sub}>TREATMENT CENTERS</text>
      <g>
        <line x1="6" y1="103" x2="52" y2="103" stroke={rule} strokeWidth="1.4" />
        <text x="62" y="108" fontFamily="Inter, sans-serif" fontWeight="600"
          fontSize="14.5" letterSpacing="2.6" fill={sub}>INTELLIGENCE OS</text>
        <line x1="228" y1="103" x2="274" y2="103" stroke={rule} strokeWidth="1.4" />
      </g>
    </svg>
  );
}

// Grayscale speed-line G. tone: 'dark' | 'light'
export function GrayscaleMark({ size = 22, tone = 'light' }) {
  const a = tone === 'light' ? '#D5DCE4' : '#9AA3AE';
  const b = tone === 'light' ? '#8A97A6' : '#4B535F';
  const gid = 'gsG' + tone;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-label="Grayscale Technologies">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={a} /><stop offset="1" stopColor={b} />
        </linearGradient>
        <mask id={gid + 'mask'}>
          <rect x="0" y="0" width="100" height="100" fill="#fff" />
          {/* speed-line cuts through the left of the G */}
          <rect x="0" y="26" width="34" height="6" fill="#000" />
          <rect x="0" y="40" width="28" height="6" fill="#000" />
          <rect x="0" y="54" width="24" height="6" fill="#000" />
        </mask>
      </defs>
      <text x="50" y="78" textAnchor="middle" fontFamily="Inter, sans-serif"
        fontWeight="800" fontSize="88" fill={'url(#' + gid + ')'}
        mask={'url(#' + gid + 'mask)'}>G</text>
    </svg>
  );
}

// "Powered by Grayscale" strip for the sidebar foot.
export function PoweredByGrayscale({ tone = 'light', collapsed = false }) {
  const label = tone === 'light' ? 'rgba(255,255,255,0.45)' : '#8291A1';
  const name = tone === 'light' ? 'rgba(255,255,255,0.78)' : '#5D6B7A';
  if (collapsed) return <GrayscaleMark size={20} tone={tone} />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <GrayscaleMark size={20} tone={tone} />
      <div style={{ lineHeight: 1.15 }}>
        <div style={{ fontSize: 8, letterSpacing: '0.16em', color: label, fontWeight: 700 }}>POWERED BY</div>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', color: name, fontWeight: 700 }}>GRAYSCALE</div>
      </div>
    </div>
  );
}
